import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import { Plus, Eye, EyeOff, Trash2, Copy, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { code: "", percent_off: 10, max_uses: "" };

/**
 * /admin/promo-codes — Owner-only promo code CRUD.
 */
export default function AdminPromoCodes() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";

  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/promo-codes`);
      setCodes(data?.codes || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load codes.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isOwner) load();
  }, [isOwner]);

  if (!isOwner) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <div className="pt-32 text-center text-neutral-400">Owner only.</div>
      </div>
    );
  }

  const upd = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  async function save() {
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        percent_off: parseInt(form.percent_off, 10) || 0,
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
      };
      await axios.post(`${API}/admin/promo-codes`, payload);
      toast.success(`Code ${payload.code} created.`);
      setForm(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed.");
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(c) {
    try {
      await axios.put(`${API}/admin/promo-codes/${c.code}`, { active: !c.active });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed.");
    }
  }

  async function remove(c) {
    if (!window.confirm(`Delete promo ${c.code}? This is permanent.`)) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${c.code}`);
      toast.success("Deleted.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  }

  function copyCode(code) {
    try {
      navigator.clipboard.writeText(code);
      toast.success(`Copied ${code} to clipboard.`);
    } catch {
      /* noop */
    }
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-promo-codes-page">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-bold">Admin</div>
            <h1 className="font-display text-3xl text-white leading-none mt-1">Promo Codes</h1>
            <p className="text-xs text-neutral-500 mt-1">
              {codes.length} codes · {codes.filter((c) => c.active).length} active
            </p>
          </div>
          <button
            onClick={() => setForm({ ...EMPTY })}
            data-testid="admin-promo-add-btn"
            className="bg-[#D4AF37] hover:bg-[#F1C84B] text-[#0A0A0A] font-bold uppercase tracking-[0.2em] text-xs py-3 px-5 transition-colors flex items-center gap-2"
          >
            <Plus size={14} /> New Code
          </button>
        </div>

        {form && (
          <div className="mb-8 bg-[#0F0F0F] border-2 border-[#D4AF37] p-5" data-testid="admin-promo-form">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-xl">New Promo Code</div>
              <button onClick={() => setForm(null)} className="text-neutral-500 hover:text-white">
                <X size={16} />
              </button>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1 block">
                  Code (3-30 chars, A-Z, 0-9, _ -)
                </label>
                <input
                  data-testid="admin-promo-code-input"
                  className="input-omni w-full uppercase" value={form.code} onChange={upd("code")}
                  placeholder="ROC50"
                  maxLength={30}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1 block">
                  Percent Off (1-100)
                </label>
                <input
                  data-testid="admin-promo-percent"
                  type="number" min="1" max="100"
                  className="input-omni w-full" value={form.percent_off} onChange={upd("percent_off")}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1 block">
                  Max Uses (blank = ∞)
                </label>
                <input
                  data-testid="admin-promo-max-uses"
                  type="number" min="1"
                  className="input-omni w-full" value={form.max_uses} onChange={upd("max_uses")}
                  placeholder="∞"
                />
              </div>
            </div>
            <button
              onClick={save}
              disabled={saving || !form.code.trim()}
              data-testid="admin-promo-save"
              className="mt-4 bg-[#D4AF37] hover:bg-[#F1C84B] text-[#0A0A0A] font-bold uppercase tracking-[0.2em] text-xs py-3 px-5 transition-colors disabled:opacity-50"
            >
              {saving ? "Creating…" : "Create Code"}
            </button>
          </div>
        )}

        {loading ? (
          <div className="text-center text-neutral-500 py-12">Loading…</div>
        ) : codes.length === 0 ? (
          <div className="text-center text-neutral-500 py-12 border border-zinc-900 bg-[#0F0F0F]">
            No promo codes yet. Click <span className="text-[#D4AF37]">New Code</span> to create your first.
          </div>
        ) : (
          <div className="space-y-2">
            {codes.map((c) => {
              const exhausted = c.max_uses && c.uses >= c.max_uses;
              return (
                <div
                  key={c.code}
                  data-testid={`admin-promo-row-${c.code}`}
                  className={
                    "bg-[#0F0F0F] border-2 p-4 flex items-center gap-3 flex-wrap " +
                    (c.active && !exhausted ? "border-zinc-800" : "border-zinc-900 opacity-60")
                  }
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-display text-2xl text-white tracking-wider">{c.code}</span>
                      <span className="text-[#D4AF37] font-bold">{c.percent_off}% OFF</span>
                      <button
                        onClick={() => copyCode(c.code)}
                        className="text-neutral-500 hover:text-[#D4AF37] inline-flex items-center gap-1 text-xs"
                      >
                        <Copy size={12} /> Copy
                      </button>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-600 mt-1">
                      {c.uses}{c.max_uses ? ` / ${c.max_uses}` : ""} uses
                      {" · "}
                      <span className={c.active && !exhausted ? "text-emerald-400" : "text-neutral-600"}>
                        {exhausted ? "exhausted" : c.active ? "active" : "disabled"}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => toggleActive(c)}
                      className="p-2 border border-zinc-800 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                      title={c.active ? "Disable" : "Enable"}
                    >
                      {c.active ? <Eye size={14} /> : <EyeOff size={14} />}
                    </button>
                    <button
                      onClick={() => remove(c)}
                      className="p-2 border border-zinc-800 hover:border-red-500 hover:text-red-500 transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
